import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { resolveUrl, API_PREFIX } from 'src/core/constants/api.constants';
import { VersionApiResponse } from '../../models/version/version-api-response.model';
import { VersionInfo } from '../../models/version/version-info.model';
import { ComponentVersion } from '../../models/version/component-version.model';
import { LocalizationService } from '@abp/ng.core';

@Injectable({ providedIn: 'root' })
export class VersionService {
  private readonly http = inject(HttpClient);
  protected readonly localizationService = inject(LocalizationService);

  getVersionInfo(): Observable<VersionInfo> {
    return this.http
      .get<VersionApiResponse>(resolveUrl(`${API_PREFIX}/version`))
      .pipe(map(response => this.mapToVersionInfo(response)));
  }

  private mapToVersionInfo(response: VersionApiResponse): VersionInfo {
    const items = response.components?.items ?? [];
    const components: ComponentVersion[] = [];
    const apiKeys: string[] = [];

    const COMPONENT_NAME_MAP: Record<string, string> = {
      'cx-api': this.localizationService.instant('Settings::API'),
      'cx-background-service': this.localizationService.instant('Settings::BGService'),
      'cx-background-worker': this.localizationService.instant('Settings::BGWorker'),
      'cx-identity': this.localizationService.instant('Settings::Identity'),
      'cx-freeswitch-api': this.localizationService.instant('Settings::FreeswitchAPI'),
      'ca-external-api': this.localizationService.instant('Settings::ConversationalAnalytics'),
      'cai-magpie-api': this.localizationService.instant('Settings::VirtualAgent'),
    };

    const BOTTOM_API_KEYS = new Set(['ca-external-api', 'cai-magpie-api']);

    function sortComponents(components: ComponentVersion[], apiKeys: string[]): ComponentVersion[] {
      return [
        ...components.filter((_, i) => !BOTTOM_API_KEYS.has(apiKeys[i])),
        ...components.filter((_, i) => BOTTOM_API_KEYS.has(apiKeys[i])),
      ];
    }

    for (const item of items) {
      const displayName = COMPONENT_NAME_MAP[item.name];
      if (displayName) {
        components.push({ name: displayName, version: item.version });
        apiKeys.push(item.name);
      }
    }

    const webVersion =
      (window as Window & { __appversions_cxweb?: string }).__appversions_cxweb ?? '-';
    components.push({ name: this.localizationService.instant('Settings::Web'), version: webVersion });
    apiKeys.push('cx-web');

    return { productVersion: response.productVersion, components: sortComponents(components, apiKeys) };
  }
}
